import { ReactNode } from 'react';
import { TeContainer, TeContainerSlim, TeIcon, TeRadio, TeTag, TeText } from '@topcon/tuicl-react';

import CompanyIcon from './Company/CompanyIcon';
import { formatOrganizationNumber } from '../utils/organizationNumberFormatter';

interface OrgCardProps {
  name: string;
  externalId?: string;
  tagText?: string;
  address?: string;
  iconURL?: string;
  selected?: boolean;
  onSelect?: () => void;
  badge?: ReactNode;
  baseBackground?: 'primary' | 'secondary';
  addressFill?: boolean;
  testId?: string;
  /**
   * Optional block rendered inside the card, below the organization row (e.g.
   * the Connect wizard's customer-number field). Switches the card to a column
   * layout; without it the card renders exactly as before.
   */
  footer?: ReactNode;
}

function OrgCard({
  name,
  externalId,
  tagText,
  address,
  iconURL,
  selected = false,
  onSelect,
  badge,
  baseBackground = 'secondary',
  addressFill = false,
  testId,
  footer,
}: OrgCardProps) {
  const backgroundColor = selected ? 'background.selected' : `background.${baseBackground}`;
  const borderColor = selected ? 'border.brand.primary' : 'border.secondary';
  const resolvedTagText =
    tagText ?? (externalId ? formatOrganizationNumber(externalId) : undefined);

  const organizationRow = (
    <>
      <TeContainerSlim gap="m" alignItems="center" grow>
        <CompanyIcon size="l" iconURL={iconURL} />
        <TeContainerSlim column gap="xs" alignItems="flex-start" grow>
          <TeContainerSlim gap="xs" alignItems="center">
            <TeText size={16} weight={500}>
              {name}
            </TeText>
            {resolvedTagText && (
              <TeTag
                size="s"
                text={resolvedTagText}
                backgroundColor="background.tertiary"
                textColor="text.primary"
                borderColor="border.secondary"
              />
            )}
          </TeContainerSlim>
          {address && (
            <TeContainer
              gap="xs"
              alignItems="center"
              backgroundColor="background.primary"
              borderColor="border.secondary"
              borderRadius="l"
              padding="xs"
              height="auto"
              fill={addressFill}
              style={{ flexShrink: 0 }}
            >
              <TeIcon name="domain" size="m" color="icon.primary" />
              <TeText size={14} color="text.primary">
                {address}
              </TeText>
            </TeContainer>
          )}
        </TeContainerSlim>
      </TeContainerSlim>
      {onSelect && (
        <TeRadio
          name="org-selection"
          value={externalId}
          checked={selected}
          techange={onSelect}
          data-testid={externalId ? `radio-${externalId}` : undefined}
        />
      )}
    </>
  );

  return (
    <TeContainer
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderRadius="l"
      // The footer variant puts the row's top inset on the row itself, so the
      // footer sits flush under it (design: `pb-16 px-16` on the card).
      padding={footer ? [0, 'm', 'm', 'm'] : ['s', 'm']}
      gap={footer ? 'xs' : 'xl'}
      column={!!footer}
      alignItems={footer ? 'stretch' : 'center'}
      height="auto"
      fill
      data-testid={testId}
      onClick={onSelect}
      style={{ position: 'relative', overflow: 'hidden', cursor: onSelect ? 'pointer' : 'default' }}
    >
      {footer ? (
        <TeContainerSlim gap="xl" alignItems="center" fill padding={['s', 0, 0, 0]}>
          {organizationRow}
        </TeContainerSlim>
      ) : (
        organizationRow
      )}
      {footer}
      {badge}
    </TeContainer>
  );
}

export default OrgCard;
export type { OrgCardProps };
